import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import {
  RequireIcon as CustomRequireIcon,
  DropDownArrowIcon,
} from "@ippon/shared-assets";
import { CategoryOption } from "./CustomCategorySelect";
import { useResponsiveMenuPlacement } from "../../hooks/useResponsiveMenuPlacement";

export interface CustomCategorySelectSingleProps {
  label: string;
  name: string;
  options: CategoryOption[];
  selectedValue: string;
  onValueChange: (value: string) => void;
  errors?: any;
  isRequired?: boolean;
  labelWidth?: string;
  inputWidth?: string;
  placeholder?: string;
  /** トリガー(選択値)の文字サイズ (px)。既定 12。 */
  fontSize?: number;
  /** 指定時、オプション先頭に「新規登録」ボタンを出す。押すと閉じてこのコールバックを呼ぶ
   *  (モーダルを開く用途)。検索+その場追加 (onAddNewOption) とは別物。 */
  onAddNew?: () => void;
  onAddNewOption?: (newOption: string) => void;
  onDeleteOption?: (optionValue: string) => void;
  /** ラベル文字列の右隣に挟む任意の React ノード (AI バッジ等) */
  labelSuffix?: React.ReactNode;
  /** ドロップダウン(検索+追加)の最小幅。トリガーが狭い時に検索入力が潰れないよう任意に広げる。 */
  menuWidth?: number | string;
  /** 選択肢が少なくキーワード検索が不要な時は false にして検索入力を隠す (既定 true)。 */
  searchable?: boolean;
  /** 空選択(プレースホルダー)で値をクリアできるか。必須項目は false で空選択を出さない (既定 true)。 */
  clearable?: boolean;
  /** 検索で一致が無い時の文言 (既定「該当する項目がありません」)。 */
  emptyText?: string;
}

const CustomCategorySelectSingle: React.FC<CustomCategorySelectSingleProps> = ({
  label,
  name,
  options,
  selectedValue,
  onValueChange,
  errors = {},
  isRequired = false,
  labelWidth = "120px",
  inputWidth = "100%",
  placeholder = "選択してください",
  fontSize = 12,
  onAddNew,
  onAddNewOption,
  labelSuffix,
  menuWidth,
  searchable = true,
  clearable = true,
  emptyText = "該当する項目がありません",
}) => {
  const searchPlaceholder = onAddNewOption ? "検索と追加" : "キーワード検索";

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchText, setSearchText] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingOption, setPendingOption] = useState("");
  const openMenu = Boolean(anchorEl);
  const listRef = useRef<HTMLDivElement>(null);
  const {
    menuMaxHeight,
    menuAnchorOrigin,
    menuTransformOrigin,
    updateMenuPlacement,
  } = useResponsiveMenuPlacement(400);

  // メニューが開いた時 / 選択値が変わった時に選択中の項目までスクロール
  useEffect(() => {
    if (!openMenu || !selectedValue) return;
    // 次フレームで DOM 反映後にスクロール
    requestAnimationFrame(() => {
      const list = listRef.current;
      if (!list) return;
      const el = list.querySelector(
        `[data-option-value="${(window as any).CSS?.escape ? (window as any).CSS.escape(selectedValue) : selectedValue}"]`,
      ) as HTMLElement | null;
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "nearest" });
      }
    });
  }, [openMenu, selectedValue, options]);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    const trigger = event.currentTarget;
    updateMenuPlacement(trigger);
    setAnchorEl(trigger);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSearchText("");
    setShowConfirmation(false);
    setPendingOption("");
  };

  const handleSelect = (value: string) => {
    onValueChange(value);
    handleCloseMenu();
  };

  const handleAddNewOptionClick = () => {
    const trimmed = searchText.trim();
    if (!trimmed || !onAddNewOption) return;

    const existing = options.find(
      (opt) => opt.label.toLowerCase() === trimmed.toLowerCase(),
    );

    if (existing) {
      handleSelect(existing.value);
      return;
    }

    // 追加 → 値を即選択。メニューは開いたまま
    onAddNewOption(trimmed);
    onValueChange(trimmed);
    setSearchText("");
    setShowConfirmation(false);
    setPendingOption("");
  };

  const handleConfirmAdd = () => {
    if (pendingOption && onAddNewOption) {
      onAddNewOption(pendingOption);
      onValueChange(pendingOption);
      setSearchText("");
      setPendingOption("");
      setShowConfirmation(false);
    }
  };

  const handleCancelAdd = () => {
    setPendingOption("");
    setShowConfirmation(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (
      e.key === "Enter" &&
      onAddNewOption &&
      searchText.trim() !== "" &&
      !showConfirmation
    ) {
      handleAddNewOptionClick();
    }
  };

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchText.toLowerCase()),
  );

  // 選択中の value に対応する label をルックアップ (無ければ value をそのまま表示)
  const selectedLabel =
    options.find((o) => String(o.value) === String(selectedValue))?.label ??
    selectedValue;

  return (
    <Box
      className="inputGroupWrapper flexCol"
      // AI 画面コンテキスト用 (getPageContext が拾う)。
      // 呼び出し側はラッパー不要で name/label/options/selectedValue が自動的に AI に伝わる。
      data-field={name || undefined}
      data-label={label || undefined}
      data-options={
        options && options.length > 0 ? JSON.stringify(options) : undefined
      }
      data-value-raw={selectedValue || undefined}
      sx={{
        display: "flex !important",
        flexDirection: "column !important",
        width: "100%",
        // エラー時はメッセージ自体が下方向のスペースになるため、外側マージンは 8px に
        ...(errors && errors[name] ? { mb: "8px !important" } : {}),
      }}
    >
      <Box
        className="inputLabel"
        sx={{
          display: "flex",
          alignItems: "center",
          width: { lg: labelWidth, xs: "100%" },
          fontSize: "12px !important",
          alignSelf: "flex-start",
          pb: "4px",
          mb: "1px",
        }}
      >
        {label}
        {/* ラベルが無い (インライン用途) 時は ※ だけが浮くので出さない。 */}
        {isRequired && label && (
          <span style={{ display: "flex", marginLeft: "4px" }}>
            <CustomRequireIcon />
          </span>
        )}
        {labelSuffix}
      </Box>
      <Box sx={{ width: "100%", maxWidth: { sm: inputWidth, xs: "100%" } }}>
        <Box
          onClick={handleOpenMenu}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: { sm: "34px", xs: "32px" },
            px: 1.25,
            bgcolor: "#fff",
            border: `1px solid ${isRequired && errors[name] ? "#d32f2f" : "#D9D9D9"}`,
            borderRadius: "8px",
            cursor: "pointer",
            "&:hover": {
              borderColor: isRequired && errors[name] ? "#d32f2f" : "#D9D9D9",
            },
          }}
        >
          <Box
            sx={{
              fontSize,
              color: selectedValue ? "#3E3E3E" : "#989898",
              flex: 1,
            }}
          >
            {selectedValue ? selectedLabel : placeholder}
          </Box>
          <DropDownArrowIcon style={{ fontSize: 14, flexShrink: 0 }} />
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleCloseMenu}
          anchorOrigin={menuAnchorOrigin}
          transformOrigin={menuTransformOrigin}
          transitionDuration={0}
          PaperProps={{
            sx: {
              // 幅はオプションの中身に合わせて広げる (max-content)。
              // ただしトリガー幅を下限にして狭くはならないようにする。
              minWidth: anchorEl?.offsetWidth || "auto",
              width: "max-content",
              maxWidth: "calc(100vw - 32px)",
              // menuWidth 指定時は最低この幅 (検索+追加の入力が潰れないように)。
              ...(menuWidth ? { minWidth: menuWidth } : {}),
              maxHeight: menuMaxHeight,
            },
          }}
        >
          {searchable && (
            <Box
              sx={{
                px: 1.5,
                py: 0.5,
                borderBottom: "1px solid #F0F0F0",
                bgcolor: "#FAFAFA",
              }}
            >
              {showConfirmation ? (
                <Box
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    minHeight: 36,
                    px: 1.25,
                    py: 0.5,
                    bgcolor: "#fff",
                    border: "1px solid #D9D9D9",
                    borderRadius: "8px",
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: "#F0F0F0",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      height: 24,
                      px: 1,
                      borderRadius: "4px",
                    }}
                  >
                    <Box sx={{ fontSize: 12, color: "#3E3E3E" }}>
                      {pendingOption}
                    </Box>
                    <Box
                      component="button"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelAdd();
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        width: 16,
                        height: 16,
                        border: "none",
                        background: "transparent",
                        borderRadius: "50%",
                        cursor: "pointer",
                        p: 0,
                        transition: "background-color 0.2s",
                        "&:hover": { backgroundColor: "#d9d9d9" },
                      }}
                      aria-label="キャンセル"
                    >
                      <CloseIcon sx={{ fontSize: 12, color: "#3E3E3E" }} />
                    </Box>
                  </Box>
                  <Box
                    component="button"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleConfirmAdd();
                    }}
                    sx={{
                      border: 0,
                      background: "transparent",
                      cursor: "pointer",
                      fontSize: 12,
                      color: "#0E9DBC",
                      fontWeight: 600,
                      textDecoration: "underline",
                      "&:hover": { color: "#0B7A8F" },
                    }}
                  >
                    追加する
                  </Box>
                </Box>
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  placeholder={searchPlaceholder}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={handleKeyPress}
                  onClick={(e) => e.stopPropagation()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: "#989898" }} />
                      </InputAdornment>
                    ),
                    endAdornment: onAddNewOption &&
                      searchText.trim() !== "" && (
                        <InputAdornment position="end">
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddNewOptionClick();
                            }}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              cursor: "pointer",
                              pr: 0.5,
                              py: 0.2,
                              borderRadius: "10px",
                              transition: "all 0.2s",
                              bgcolor: "#f7fbfd",
                              border: "1px solid #0B9DBD",
                              color: "#0B9DBD",
                              fontFamily: '"Noto Sans JP"',
                              fontWeight: 700,
                              "&:hover": {
                                bgcolor: "#e9f6fa",
                                borderColor: "#0B9DBD",
                              },
                            }}
                          >
                            <IconButton
                              size="small"
                              disableRipple
                              sx={{
                                p: 0.4,
                                color: "#0B9DBD",
                                borderRadius: "6px",
                                transition: "all 0.2s",
                              }}
                              aria-label="新規追加"
                              title="新規追加"
                            >
                              <ArrowUpwardIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                            <Box
                              component="span"
                              sx={{
                                fontSize: 12,
                                color: "#0B9DBD",
                                fontWeight: 700,
                                transition: "all 0.2s",
                                pr: 0.7,
                              }}
                            >
                              追加
                            </Box>
                          </Box>
                        </InputAdornment>
                      ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: 12,
                      height: 36,
                      borderRadius: "8px",
                      "& fieldset": { borderColor: "#D9D9D9" },
                      "&:hover fieldset": { borderColor: "#D9D9D9" },
                      "&.Mui-focused fieldset": { borderColor: "#D9D9D9" },
                    },
                  }}
                />
              )}
            </Box>
          )}

          <Box ref={listRef} sx={{ maxHeight: "250px", overflowY: "auto" }}>
            {/* 新規登録: 押すと閉じて onAddNew (モーダル) を呼ぶ。一覧の先頭に固定。 */}
            {onAddNew && (
              <MenuItem
                onClick={() => {
                  handleCloseMenu();
                  requestAnimationFrame(() => onAddNew());
                }}
                sx={{
                  fontSize: 12,
                  py: 1,
                  // ＋ の開始位置をプレースホルダー/オプションと揃える (既定16px→14px)
                  pl: "14px",
                  color: "#0B9DBD",
                  fontWeight: 700,
                  borderBottom: "1px solid #F0F0F0",
                  // スクロールしても一覧の先頭に固定 (背景白でオプションを透けさせない)
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  backgroundColor: "#fff",
                  "&:hover": { bgcolor: "#f0fafd" },
                }}
              >
                ＋ 新規登録
              </MenuItem>
            )}
            {/* clearable=false の時は空選択(プレースホルダー)を出さない。
                出すと選択で value="" になり必須項目を空に更新できてしまう。 */}
            {!searchText && clearable && (
              <MenuItem
                onClick={() => handleSelect("")}
                sx={{ fontSize: 12, py: 1, color: "#989898" }}
              >
                {placeholder}
              </MenuItem>
            )}
            {filteredOptions.length === 0 ? (
              <MenuItem disabled sx={{ fontSize: 12, py: 1 }}>
                {emptyText}
              </MenuItem>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = selectedValue === opt.value;
                return (
                  <MenuItem
                    key={opt.value}
                    data-option-value={opt.value}
                    onClick={() => handleSelect(opt.value)}
                    sx={{
                      fontSize: 12,
                      py: 1,
                      bgcolor: isSelected ? "#f0fafd" : "transparent",
                      "&:hover": {
                        bgcolor: isSelected ? "#e3f5fb" : undefined,
                      },
                    }}
                  >
                    {opt.rightLabel ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: "100%",
                          gap: 1,
                        }}
                      >
                        <span>{opt.label}</span>
                        <span style={{ color: "#989898" }}>
                          {opt.rightLabel}
                        </span>
                      </Box>
                    ) : (
                      opt.label
                    )}
                  </MenuItem>
                );
              })
            )}
          </Box>
        </Menu>
      </Box>

      {errors && errors[name] && (
        <Box
          sx={{
            color: "#d32f2f",
            fontSize: "0.75rem",
            lineHeight: 1.66,
            mt: "3px",
            ml: "14px",
          }}
        >
          {errors[name].message}
        </Box>
      )}
    </Box>
  );
};

export default CustomCategorySelectSingle;
